import ExcelJS from 'exceljs'
import type { PlanComparisonResponse, PlanComparisonRow } from './types.ts'

const MONTH_LABELS = ['Jan', 'Feb', 'Mär', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Dez']

// Spalten: A=Position, B=Jahr, C..N=Jan..Dez. monthCol(0)='C', monthCol(11)='N'.
const monthCol = (m: number) => String.fromCharCode(67 + m)

/** 1-basierter Spalten-Index → Excel-Buchstabe (A, B, …, Z, AA, AB, …). */
function colLetter(col: number): string {
  let s = ''
  let n = col
  while (n > 0) {
    const rem = (n - 1) % 26
    s = String.fromCharCode(65 + rem) + s
    n = Math.floor((n - 1) / 26)
  }
  return s
}

// Aggregat-Modus identisch zur Bildschirm-Anzeige (PlanEditorPage.annualTotals).
const FIRST_LAST_BY_CODE: Record<string, 'first' | 'last'> = {
  liq_anfangsbestand: 'first',
  liq_endbestand: 'last',
}

/** Jahres-Aggregatmodus: Bilanzpositionen sind Bestaende -> Dezember ('last'). */
function firstLastMode(code: string): 'first' | 'last' | undefined {
  if (code.startsWith('bs_')) return 'last'
  return FIRST_LAST_BY_CODE[code]
}

/**
 * Excel-Formel-Bauplan pro Aggregat-/Computed-Position. Operanden sind
 * Codes anderer Positionen — werden beim Schreiben in Zell-Adressen
 * (z.B. "C12") des aktuellen Monats aufgeloest. `monthOffset` referenziert
 * einen anderen Monat (z.B. -2 fuer USt-Versatz).
 *
 * Die Formeln spiegeln 1:1 die Server-Logik aus
 * apps/backend/src/lib/liquidity-planning/aggregates.ts wieder.
 */
type Operand =
  | { kind: 'ref'; code: string; monthOffset?: number; sign: '+' | '-' }
  | { kind: 'static' } // Wert wird statisch geschrieben (kein Formel-Eintrag)
  | { kind: 'prevEnd'; code: string } // Vormonats-Endbestand (fuer liq_anfangsbestand[m>0])

const FORMULAS: Record<string, Operand[]> = {
  // ─ GuV ─────────────────────────────────────────────────────────────────
  umsatzerloese: [
    { kind: 'ref', code: 'ums_erloese_gl',       sign: '+' },
    { kind: 'ref', code: 'ums_vertraege',        sign: '+' },
    { kind: 'ref', code: 'ums_dienstleistungen', sign: '+' },
    { kind: 'ref', code: 'ums_auftragsbestand',  sign: '+' },
    { kind: 'ref', code: 'ums_angebotsbestand',  sign: '+' },
    { kind: 'ref', code: 'ums_aquise_bestand',   sign: '+' },
    { kind: 'ref', code: 'ums_aquise_neu',       sign: '+' },
  ],
  rohertrag: [
    { kind: 'ref', code: 'umsatzerloese', sign: '+' },
    { kind: 'ref', code: 'wareneinsatz',  sign: '-' },
  ],
  ebitda: [
    { kind: 'ref', code: 'rohertrag',          sign: '+' },
    { kind: 'ref', code: 'sonstige_ertraege',  sign: '+' },
    { kind: 'ref', code: 'personalkosten',     sign: '-' },
    { kind: 'ref', code: 'raumkosten',         sign: '-' },
    { kind: 'ref', code: 'kfz_kosten',         sign: '-' },
    { kind: 'ref', code: 'werbe_reisekosten',  sign: '-' },
    { kind: 'ref', code: 'reparaturen',        sign: '-' },
    { kind: 'ref', code: 'sonstige_kosten',    sign: '-' },
    { kind: 'ref', code: 'versicherungen',     sign: '-' },
    { kind: 'ref', code: 'kfz_steuer',         sign: '+' }, // siehe aggregates.ts
  ],
  ebit: [
    { kind: 'ref', code: 'ebitda', sign: '+' },
    { kind: 'ref', code: 'afa',    sign: '-' },
  ],
  ebt: [
    { kind: 'ref', code: 'ebit',         sign: '+' },
    { kind: 'ref', code: 'zinsaufwand',  sign: '-' },
    { kind: 'ref', code: 'zinsertraege', sign: '+' },
  ],
  // 'steuern' bleibt statisch (haengt vom server-konfigurierten Steuersatz ab,
  // den wir im Frontend-Response nicht haben).
  jahresueberschuss: [
    { kind: 'ref', code: 'ebt',     sign: '+' },
    { kind: 'ref', code: 'steuern', sign: '-' },
  ],

  // ─ Cashflow ────────────────────────────────────────────────────────────
  cf_jahresueberschuss: [{ kind: 'ref', code: 'jahresueberschuss', sign: '+' }],
  cf_afa_ruecknahme:    [{ kind: 'ref', code: 'afa',               sign: '+' }],
  // cf_ust_einnahmen / cf_ust_vorsteuer haengen vom USt-Satz ab → statisch lassen.
  // cf_ust_zahllast referenziert die beiden, geht also als Formel.
  cf_ust_zahllast: [
    { kind: 'ref', code: 'cf_ust_einnahmen', sign: '+' },
    { kind: 'ref', code: 'cf_ust_vorsteuer', sign: '-' },
  ],
  cf_operativ: [
    { kind: 'ref', code: 'cf_jahresueberschuss',  sign: '+' },
    { kind: 'ref', code: 'cf_afa_ruecknahme',     sign: '+' },
    { kind: 'ref', code: 'cf_ust_zahllast',       sign: '-' },
    { kind: 'ref', code: 'cf_debitoren_eingang',  sign: '+' },
    { kind: 'ref', code: 'cf_kreditoren_ausgang', sign: '-' },
    { kind: 'ref', code: 'cf_ratenzahlungen',     sign: '-' },
  ],
  cf_liquiditaetswirksam: [
    { kind: 'ref', code: 'cf_operativ',         sign: '+' },
    { kind: 'ref', code: 'cf_tilgung_darlehen', sign: '-' },
    { kind: 'ref', code: 'cf_invest',           sign: '-' },
    { kind: 'ref', code: 'cf_gesellschafter',   sign: '+' },
  ],

  // ─ Liquiditaet ──────────────────────────────────────────────────────────
  // liq_anfangsbestand: m=0 statisch, m>0 = Endbestand[m-1]
  liq_anfangsbestand: [{ kind: 'prevEnd', code: 'liq_endbestand' }],
  liq_cashflow:       [{ kind: 'ref', code: 'cf_liquiditaetswirksam', sign: '+' }],
  liq_endbestand: [
    { kind: 'ref', code: 'liq_anfangsbestand', sign: '+' },
    { kind: 'ref', code: 'liq_cashflow',       sign: '+' },
  ],
}

/** Formel oder Wert fuer eine Zelle bauen. */
function cellFormula(
  code: string,
  m: number,
  rowByCode: Map<string, number>,
): { formula: string } | null {
  const ops = FORMULAS[code]
  if (!ops) return null

  // Sonderfall: liq_anfangsbestand → m=0 statisch (Anfangsbestand der Bank),
  // m>0 Verkettung mit Endbestand des Vormonats.
  if (ops[0]?.kind === 'prevEnd') {
    if (m === 0) return null
    const refRow = rowByCode.get(ops[0].code)
    if (!refRow) return null
    return { formula: `${monthCol(m - 1)}${refRow}` }
  }

  const parts: string[] = []
  for (const op of ops) {
    if (op.kind !== 'ref') continue
    const refRow = rowByCode.get(op.code)
    if (!refRow) return null // referenced row not exported → fall back to static
    const offset = op.monthOffset ?? 0
    const targetMonth = m + offset
    if (targetMonth < 0 || targetMonth > 11) return null
    parts.push(`${op.sign}${monthCol(targetMonth)}${refRow}`)
  }
  if (parts.length === 0) return null
  // Erstes Vorzeichen weg, falls '+'
  const expr = parts.join('').replace(/^\+/, '')
  return { formula: expr }
}

const FILL = {
  sectionGuv:      'FFDBEAFE', // blue-100
  sectionBilanz:   'FFFEF3C7', // amber-100
  sectionCashflow: 'FFE0E7FF', // indigo-100
  sectionLiqui:    'FFCCFBF1', // teal-100
  aggregate:       'FFF1F5F9', // slate-100
  header:          'FF1E3A8A', // blue-900
  yearCol:         'FFF8FAFC', // slate-50
  empty:           'FFFFFFFF',
}

// Reihenfolge der Sektionen in allen Export-Varianten (inkl. Bilanz, Phase 4).
type Section = 'guv' | 'bilanz' | 'cashflow' | 'liqui'
const ALL_SECTIONS: Section[] = ['guv', 'bilanz', 'cashflow', 'liqui']
function sectionFill(section: Section): string {
  return section === 'guv' ? FILL.sectionGuv
    : section === 'bilanz' ? FILL.sectionBilanz
    : section === 'cashflow' ? FILL.sectionCashflow
    : FILL.sectionLiqui
}

function thinBorder() {
  return {
    top:    { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
    bottom: { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
    left:   { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
    right:  { style: 'thin' as const, color: { argb: 'FFE2E8F0' } },
  }
}

const SECTION_LABELS: Record<string, string> = {
  guv:      'Gewinn- und Verlustrechnung (BWA)',
  bilanz:   'Bilanz',
  cashflow: 'Cashflow-Ableitung (Indirekt)',
  liqui:    'Liquiditätsentwicklung',
}

function applyHeaderRow(ws: ExcelJS.Worksheet, year: number) {
  // Row 1: Titel-Banner
  ws.mergeCells(1, 1, 1, 14)
  const title = ws.getCell(1, 1)
  title.value = `Liquiditätsplanung — Jahr ${year}`
  title.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }
  title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.header } }
  title.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
  ws.getRow(1).height = 22

  // Row 2: Spalten-Header
  const head = ws.getRow(2)
  head.values = ['Position', 'Jahr', ...MONTH_LABELS]
  head.height = 22
  head.eachCell((cell, col) => {
    cell.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }
    cell.alignment = {
      vertical: 'middle',
      horizontal: col === 1 ? 'left' : 'right',
      indent: col === 1 ? 1 : 0,
    }
    cell.border = thinBorder()
  })
}

function setColumnWidths(ws: ExcelJS.Worksheet) {
  ws.getColumn(1).width = 42
  ws.getColumn(2).width = 14
  for (let c = 3; c <= 14; c++) ws.getColumn(c).width = 13
}

const NUM_FMT = '#,##0;[Red]-#,##0;-'

/**
 * Schreibt eine Plan-Zeile mit 12 Monatswerten + Jahres-Aggregat.
 * Aggregat-/Computed-Positionen erhalten Excel-Formeln (siehe FORMULAS),
 * Blatt-Positionen werden mit dem aktuellen SOLL-Wert beschrieben.
 *
 * Jahres-Spalte (B) ist immer eine Formel:
 *   - 'first': = C{row}     (Januar)
 *   - 'last':  = N{row}     (Dezember)
 *   - sonst:   = SUM(C..N)
 */
function writeRow(
  ws: ExcelJS.Worksheet,
  row: PlanComparisonRow,
  rowIdx: number,
  rowByCode: Map<string, number>,
) {
  const xlRow = ws.getRow(rowIdx)
  const labelCell = xlRow.getCell(1)
  labelCell.value = row.label
  labelCell.font = { bold: row.isAggregate }
  labelCell.alignment = { vertical: 'middle', horizontal: 'left', indent: row.parentCode ? 2 : 1 }

  // Monatswerte
  for (let m = 0; m < 12; m++) {
    const cell = xlRow.getCell(3 + m)
    const formula = cellFormula(row.code, m, rowByCode)
    const sollValue = row.monthly[m]?.soll ?? 0

    if (formula) {
      cell.value = { formula: formula.formula, result: round2(sollValue) }
    } else {
      cell.value = round2(sollValue)
    }
    cell.numFmt = NUM_FMT
    cell.alignment = { vertical: 'middle', horizontal: 'right' }
    cell.border = thinBorder()
    if (row.isAggregate) {
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.aggregate } }
      cell.font = { bold: true }
    }
  }

  // Jahres-Spalte (B)
  const yearCell = xlRow.getCell(2)
  const mode = firstLastMode(row.code)
  if (mode === 'first') {
    yearCell.value = { formula: `C${rowIdx}`, result: round2(row.monthly[0]?.soll ?? 0) }
  } else if (mode === 'last') {
    yearCell.value = { formula: `N${rowIdx}`, result: round2(row.monthly[11]?.soll ?? 0) }
  } else {
    const sum = row.monthly.reduce((acc, c) => acc + (c.soll ?? 0), 0)
    yearCell.value = { formula: `SUM(C${rowIdx}:N${rowIdx})`, result: round2(sum) }
  }
  yearCell.numFmt = NUM_FMT
  yearCell.alignment = { vertical: 'middle', horizontal: 'right' }
  yearCell.border = thinBorder()
  yearCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.yearCol } }
  yearCell.font = { bold: true }

  labelCell.border = thinBorder()
  if (row.isAggregate) {
    labelCell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.aggregate } }
  }
}

function writeSectionHeader(
  ws: ExcelJS.Worksheet,
  section: Section,
  rowIdx: number,
) {
  ws.mergeCells(rowIdx, 1, rowIdx, 14)
  const cell = ws.getCell(rowIdx, 1)
  cell.value = SECTION_LABELS[section]
  const fill = sectionFill(section)
  cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } }
  cell.font = { bold: true, color: { argb: 'FF0F172A' }, size: 11 }
  cell.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
  cell.border = thinBorder()
  ws.getRow(rowIdx).height = 20
}

function round2(v: number): number {
  return Math.round(v * 100) / 100
}

function buildSollSheet(wb: ExcelJS.Workbook, response: PlanComparisonResponse, sheetName = 'SOLL Plan'): ExcelJS.Worksheet {
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: 'frozen', xSplit: 2, ySplit: 2 }],
  })
  setColumnWidths(ws)
  applyHeaderRow(ws, response.plan.year)

  const sections = ALL_SECTIONS
  // Zwei-Pass: erst Row-Index pro Code ermitteln, damit Formeln nach
  // vorne UND hinten verweisen koennen.
  const rowByCode = new Map<string, number>()
  let cursor = 3
  for (const section of sections) {
    cursor++ // section header
    for (const row of response.matrix.filter((r) => r.section === section)) {
      rowByCode.set(row.code, cursor)
      cursor++
    }
  }

  // Pass 2: tatsaechlich schreiben
  cursor = 3
  for (const section of sections) {
    writeSectionHeader(ws, section, cursor)
    cursor++
    for (const row of response.matrix.filter((r) => r.section === section)) {
      writeRow(ws, row, cursor, rowByCode)
      cursor++
    }
  }

  ws.autoFilter = { from: { row: 2, column: 1 }, to: { row: 2, column: 14 } }
  return ws
}

/**
 * Vergleichs-Sheet mit Spalten je Monat: SOLL, IST, Δ. Δ ist eine Formel
 * (=IST-SOLL), damit der Excel-User SOLL/IST nachtraeglich ueberschreiben
 * und das Delta automatisch sehen kann.
 */
function buildVergleichSheet(wb: ExcelJS.Workbook, response: PlanComparisonResponse, sheetName = 'Vergleich'): ExcelJS.Worksheet {
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }],
  })
  ws.getColumn(1).width = 42
  for (let c = 2; c <= 1 + 12 * 3; c++) ws.getColumn(c).width = 11

  // Row 1: Banner
  const totalCols = 1 + 12 * 3
  ws.mergeCells(1, 1, 1, totalCols)
  const title = ws.getCell(1, 1)
  title.value = `SOLL/IST-Vergleich — Jahr ${response.plan.year}`
  title.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }
  title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.header } }
  title.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
  ws.getRow(1).height = 22

  // Row 2: Monats-Banner (gemerged ueber je 3 Spalten)
  ws.getCell(2, 1).value = ''
  for (let m = 0; m < 12; m++) {
    const startCol = 2 + m * 3
    ws.mergeCells(2, startCol, 2, startCol + 2)
    const c = ws.getCell(2, startCol)
    c.value = `${MONTH_LABELS[m]} ${response.plan.year}`
    c.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }
    c.alignment = { vertical: 'middle', horizontal: 'center' }
    c.border = thinBorder()
  }
  ws.getRow(2).height = 20

  // Row 3: Sub-Header (SOLL / IST / Δ)
  const sub = ws.getRow(3)
  sub.getCell(1).value = 'Position'
  sub.getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' } }
  sub.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }
  sub.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
  sub.getCell(1).border = thinBorder()
  for (let m = 0; m < 12; m++) {
    const startCol = 2 + m * 3
    const labels = ['SOLL', 'IST', 'Δ']
    for (let i = 0; i < 3; i++) {
      const cell = sub.getCell(startCol + i)
      cell.value = labels[i]
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF475569' } }
      cell.alignment = { vertical: 'middle', horizontal: 'center' }
      cell.border = thinBorder()
    }
  }
  sub.height = 18

  // Daten-Zeilen
  const sections = ALL_SECTIONS
  let cursor = 4
  for (const section of sections) {
    ws.mergeCells(cursor, 1, cursor, totalCols)
    const sec = ws.getCell(cursor, 1)
    sec.value = SECTION_LABELS[section]
    const fill = sectionFill(section)
    sec.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: fill } }
    sec.font = { bold: true, color: { argb: 'FF0F172A' } }
    sec.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
    cursor++

    for (const row of response.matrix.filter((r) => r.section === section)) {
      const xlRow = ws.getRow(cursor)
      const label = xlRow.getCell(1)
      label.value = row.label
      label.font = { bold: row.isAggregate }
      label.alignment = { vertical: 'middle', horizontal: 'left', indent: row.parentCode ? 2 : 1 }
      label.border = thinBorder()
      if (row.isAggregate) {
        label.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.aggregate } }
      }

      for (let m = 0; m < 12; m++) {
        const startCol = 2 + m * 3
        const cell = row.monthly[m]
        const sollCell = xlRow.getCell(startCol)
        const istCell  = xlRow.getCell(startCol + 1)
        const dCell    = xlRow.getCell(startCol + 2)

        sollCell.value = round2(cell?.soll ?? 0)
        if (cell?.ist !== null && cell?.ist !== undefined) {
          istCell.value = round2(cell.ist)
        } else {
          istCell.value = ''
        }
        // Delta = IST - SOLL (nur wenn IST vorhanden)
        const sollAddr = `${colLetter(startCol)}${cursor}`
        const istAddr  = `${colLetter(startCol + 1)}${cursor}`
        if (cell?.ist !== null && cell?.ist !== undefined) {
          dCell.value = {
            formula: `${istAddr}-${sollAddr}`,
            result: round2((cell.ist) - (cell.soll ?? 0)),
          }
        } else {
          dCell.value = ''
        }

        for (const c of [sollCell, istCell, dCell]) {
          c.numFmt = NUM_FMT
          c.alignment = { vertical: 'middle', horizontal: 'right' }
          c.border = thinBorder()
          if (row.isAggregate) {
            c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.aggregate } }
            c.font = { bold: true }
          }
        }
      }
      cursor++
    }
  }

  return ws
}

/**
 * Banken-Report: pro Monat zwei Spalten PLAN und IST nebeneinander; die
 * Abweichung (IST − PLAN) steht in einer kompakten Zeile direkt UNTER der
 * Planzahl (eigene Δ-Zeile je Position). Layout fuer Bank-Vorlagen.
 */
function buildBankenSheet(wb: ExcelJS.Workbook, response: PlanComparisonResponse, sheetName = 'Banken'): ExcelJS.Worksheet {
  const ws = wb.addWorksheet(sheetName, {
    views: [{ state: 'frozen', xSplit: 1, ySplit: 3 }],
  })
  const planCol = (m: number) => 2 + m * 2
  const istCol = (m: number) => 3 + m * 2
  const totalCols = 1 + 12 * 2
  ws.getColumn(1).width = 40
  for (let c = 2; c <= totalCols; c++) ws.getColumn(c).width = 12

  // Row 1: Banner
  ws.mergeCells(1, 1, 1, totalCols)
  const title = ws.getCell(1, 1)
  title.value = `Banken-Report PLAN/IST — ${response.plan.companyKey} · ${response.plan.year}`
  title.font = { bold: true, size: 13, color: { argb: 'FFFFFFFF' } }
  title.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.header } }
  title.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
  ws.getRow(1).height = 22

  // Row 2: Monats-Banner (je 2 Spalten gemerged)
  for (let m = 0; m < 12; m++) {
    ws.mergeCells(2, planCol(m), 2, istCol(m))
    const c = ws.getCell(2, planCol(m))
    c.value = `${MONTH_LABELS[m]} ${response.plan.year}`
    c.font = { bold: true, color: { argb: 'FFFFFFFF' } }
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }
    c.alignment = { vertical: 'middle', horizontal: 'center' }
    c.border = thinBorder()
  }
  ws.getRow(2).height = 20

  // Row 3: Sub-Header PLAN / IST
  const sub = ws.getRow(3)
  sub.getCell(1).value = 'Position'
  sub.getCell(1).font = { bold: true, color: { argb: 'FFFFFFFF' } }
  sub.getCell(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF334155' } }
  sub.getCell(1).alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
  sub.getCell(1).border = thinBorder()
  for (let m = 0; m < 12; m++) {
    for (const [col, lbl] of [[planCol(m), 'PLAN'], [istCol(m), 'IST']] as const) {
      const cell = sub.getCell(col)
      cell.value = lbl
      cell.font = { bold: true, color: { argb: 'FFFFFFFF' }, size: 9 }
      cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF475569' } }
      cell.alignment = { vertical: 'middle', horizontal: 'center' }
      cell.border = thinBorder()
    }
  }
  sub.height = 18

  let cursor = 4
  for (const section of ALL_SECTIONS) {
    const sectionRows = response.matrix.filter((r) => r.section === section)
    if (sectionRows.length === 0) continue
    ws.mergeCells(cursor, 1, cursor, totalCols)
    const sec = ws.getCell(cursor, 1)
    sec.value = SECTION_LABELS[section]
    sec.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: sectionFill(section) } }
    sec.font = { bold: true, color: { argb: 'FF0F172A' } }
    sec.alignment = { vertical: 'middle', horizontal: 'left', indent: 1 }
    cursor++

    for (const row of sectionRows) {
      const valueRow = ws.getRow(cursor)
      const deltaRow = ws.getRow(cursor + 1)
      const label = valueRow.getCell(1)
      label.value = row.label
      label.font = { bold: row.isAggregate }
      label.alignment = { vertical: 'middle', horizontal: 'left', indent: row.parentCode ? 2 : 1 }
      label.border = thinBorder()
      if (row.isAggregate) label.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.aggregate } }

      const dLabel = deltaRow.getCell(1)
      dLabel.value = '↳ Abweichung'
      dLabel.font = { italic: true, size: 8, color: { argb: 'FF94A3B8' } }
      dLabel.alignment = { vertical: 'middle', horizontal: 'right', indent: 1 }

      for (let m = 0; m < 12; m++) {
        const cell = row.monthly[m]
        const plan = round2(cell?.soll ?? 0)
        const hasIst = cell?.ist !== null && cell?.ist !== undefined
        const pCell = valueRow.getCell(planCol(m))
        const iCell = valueRow.getCell(istCol(m))
        pCell.value = plan
        iCell.value = hasIst ? round2(cell!.ist as number) : ''
        for (const c of [pCell, iCell]) {
          c.numFmt = NUM_FMT
          c.alignment = { vertical: 'middle', horizontal: 'right' }
          c.border = thinBorder()
          if (row.isAggregate) { c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: FILL.aggregate } }; c.font = { bold: true } }
        }
        // Abweichung UNTER der Planzahl (Δ-Zeile, Plan-Spalte)
        const dCell = deltaRow.getCell(planCol(m))
        if (hasIst) {
          const dv = round2((cell!.ist as number) - plan)
          dCell.value = { formula: `${colLetter(istCol(m))}${cursor}-${colLetter(planCol(m))}${cursor}`, result: dv }
        } else {
          dCell.value = ''
        }
        dCell.numFmt = NUM_FMT
        dCell.font = { italic: true, size: 9, color: { argb: 'FF94A3B8' } }
        dCell.alignment = { vertical: 'middle', horizontal: 'right' }
      }
      deltaRow.height = 13
      cursor += 2
    }
  }

  return ws
}

function downloadBuffer(buf: ArrayBuffer, filename: string) {
  const blob = new Blob([buf], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  })
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = filename
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

function safeFilenamePart(s: string): string {
  return s.replace(/[^\w.-]+/g, '_').replace(/^_+|_+$/g, '') || 'plan'
}

/**
 * Exportiert den Liquiditaetsplan als gestylte .xlsx-Datei mit zwei Tabs:
 *   1. "SOLL Plan" — Monatswerte mit Excel-Formeln fuer alle Aggregate
 *      (Rohertrag, EBITDA, EBIT, EBT, Jahresueberschuss, Cashflow-Ableitung,
 *      Liquiditaets-Verkettung). Blatt-Werte als statische Zahlen — wer sie
 *      aendert, sieht direkt die Auswirkung auf alle abhaengigen Aggregate.
 *   2. "Vergleich" — SOLL/IST/Δ pro Monat, Δ als Formel =IST-SOLL.
 */
async function buildExcelBuffer(
  datasets: PlanComparisonResponse[],
  layout: ExportLayout,
): Promise<ArrayBuffer> {
  const wb = new ExcelJS.Workbook()
  wb.creator = 'SystemhausManager'
  wb.created = new Date()
  const multi = datasets.length > 1

  for (const ds of datasets) {
    const y = ds.plan.year
    if (layout === 'banken') {
      buildBankenSheet(wb, ds, multi ? `Banken ${y}` : 'Banken')
    } else {
      buildSollSheet(wb, ds, multi ? `SOLL ${y}` : 'SOLL Plan')
      buildVergleichSheet(wb, ds, multi ? `Vergleich ${y}` : 'Vergleich')
    }
  }
  return (await wb.xlsx.writeBuffer()) as ArrayBuffer
}


// ─── PDF Export ─────────────────────────────────────────────────────────
//
// Statt einer dedizierten PDF-Lib (jspdf/pdfkit — nicht in den deps,
// wuerden den Bundle aufblaehen) oeffnen wir ein neues Fenster mit gestylter
// HTML-Tabelle und triggern window.print(). Der User waehlt im Druck-Dialog
// "Als PDF speichern" — alle Browser unterstuetzen das nativ.

const EUR_FMT = new Intl.NumberFormat('de-DE', {
  style: 'currency', currency: 'EUR', maximumFractionDigits: 0,
})

function formatCellValue(v: number | null): string {
  if (v === null || v === undefined) return '–'
  if (Math.abs(v) < 0.5) return '–'
  return EUR_FMT.format(v)
}

export type ExportFormat = 'excel' | 'pdf'
export type ExportLayout = 'standard' | 'banken'

// Standard-PDF-Tabelle (PLAN/IST untereinander je Monatszelle) fuer ein Jahr.
function pdfTableStandard(comparison: PlanComparisonResponse): string {
  const rowsHtml = ALL_SECTIONS.map((section) => {
    const sectionRows = comparison.matrix.filter((r) => r.section === section)
    if (sectionRows.length === 0) return ''
    const sectionRowHtml = sectionRows.map((row) => {
      const cells = row.monthly.map((c) => {
        const v = c.ist !== null ? c.ist : c.soll
        const isIst = c.ist !== null
        return `<td class="num ${isIst ? 'ist' : 'soll'}">${formatCellValue(v)}</td>`
      }).join('')
      let yearTotal = 0
      let hasIst = false
      let istSum = 0
      for (const c of row.monthly) {
        yearTotal += c.soll ?? 0
        if (c.ist !== null) { hasIst = true; istSum += c.ist }
      }
      const mode = firstLastMode(row.code)
      let yearStr: string
      if (mode === 'first') yearStr = formatCellValue(row.monthly[0]?.soll ?? 0)
      else if (mode === 'last') yearStr = formatCellValue(row.monthly[11]?.soll ?? 0)
      else yearStr = hasIst
        ? `${formatCellValue(istSum)} <span class="muted">(P ${formatCellValue(yearTotal)})</span>`
        : formatCellValue(yearTotal)

      const aggregateClass = row.isAggregate ? 'aggregate' : ''
      const indentClass = row.parentCode ? 'indent' : ''
      return `
        <tr class="${aggregateClass}">
          <td class="label ${indentClass}">${escapeHtml(row.label)}</td>
          <td class="num year">${yearStr}</td>
          ${cells}
        </tr>`
    }).join('')
    return `
      <tr class="section section-${section}">
        <td colspan="14">${escapeHtml(SECTION_LABELS[section])}</td>
      </tr>
      ${sectionRowHtml}`
  }).join('')

  return `
  <section class="year-block">
    ${pdfHeaderBlock(comparison)}
    <table>
      <thead>
        <tr>
          <th style="width: 22%;">Position</th>
          <th style="width: 8%;">Jahr</th>
          ${MONTH_LABELS.map((m) => `<th>${m}</th>`).join('')}
        </tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <div class="legend">
      <span><span class="dot" style="background: #1d4ed8;"></span>SOLL (offener Monat / Plan)</span>
      <span><span class="dot" style="background: #0f172a;"></span>IST (abgeschlossener Monat aus SAP)</span>
    </div>
  </section>`
}

// Banken-PDF-Tabelle: pro Monat PLAN und IST nebeneinander; Abweichung
// (IST − PLAN) klein UNTER der Planzahl.
function pdfTableBanken(comparison: PlanComparisonResponse): string {
  const monthHeads = MONTH_LABELS.map((m) => `<th colspan="2" class="month-head">${m}</th>`).join('')
  const subHeads = MONTH_LABELS.map(() => `<th class="sub">Plan</th><th class="sub">Ist</th>`).join('')

  const rowsHtml = ALL_SECTIONS.map((section) => {
    const sectionRows = comparison.matrix.filter((r) => r.section === section)
    if (sectionRows.length === 0) return ''
    const sectionRowHtml = sectionRows.map((row) => {
      const cells = row.monthly.map((c) => {
        const plan = c.soll ?? 0
        const hasIst = c.ist !== null && c.ist !== undefined
        const delta = hasIst ? (c.ist as number) - plan : null
        const deltaHtml = delta !== null && Math.abs(delta) >= 0.5
          ? `<div class="delta ${delta > 0 ? 'pos' : 'neg'}">${delta > 0 ? '+' : ''}${formatCellValue(delta)}</div>`
          : ''
        return `<td class="num plan">${formatCellValue(plan)}${deltaHtml}</td>` +
               `<td class="num ist">${hasIst ? formatCellValue(c.ist) : '–'}</td>`
      }).join('')
      const aggregateClass = row.isAggregate ? 'aggregate' : ''
      const indentClass = row.parentCode ? 'indent' : ''
      return `<tr class="${aggregateClass}"><td class="label ${indentClass}">${escapeHtml(row.label)}</td>${cells}</tr>`
    }).join('')
    return `<tr class="section section-${section}"><td colspan="25">${escapeHtml(SECTION_LABELS[section])}</td></tr>${sectionRowHtml}`
  }).join('')

  return `
  <section class="year-block">
    ${pdfHeaderBlock(comparison, 'Banken-Report PLAN/IST')}
    <table class="banken">
      <thead>
        <tr><th rowspan="2" style="width: 16%;">Position</th>${monthHeads}</tr>
        <tr>${subHeads}</tr>
      </thead>
      <tbody>${rowsHtml}</tbody>
    </table>
    <div class="legend">
      <span><span class="dot" style="background: #1d4ed8;"></span>PLAN</span>
      <span><span class="dot" style="background: #0f172a;"></span>IST (aus SAP)</span>
      <span class="muted">Abweichung (IST − PLAN) klein unter der Planzahl.</span>
    </div>
  </section>`
}

function pdfHeaderBlock(comparison: PlanComparisonResponse, prefix = 'Liquiditätsplanung'): string {
  const plan = comparison.plan
  return `
  <header>
    <h1>${escapeHtml(prefix)} — ${escapeHtml(plan.companyKey)} · ${plan.year}</h1>
    <div class="meta">
      Faktor: ${plan.baseFactorPercent >= 0 ? '+' : ''}${plan.baseFactorPercent.toFixed(2)} % ·
      erstellt am ${new Date().toLocaleString('de-DE')}
    </div>
  </header>`
}

function pdfShell(titleText: string, blocks: string[]): string {
  return `<!DOCTYPE html>
<html lang="de">
<head>
<meta charset="UTF-8" />
<title>${escapeHtml(titleText)}</title>
<style>
  @page { size: A3 landscape; margin: 12mm; }
  * { box-sizing: border-box; }
  body {
    font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
    font-size: 9px;
    color: #0f172a;
    margin: 0;
  }
  header { margin-bottom: 8mm; }
  h1 { font-size: 16px; margin: 0 0 4px; color: #1e3a8a; }
  .meta { color: #64748b; font-size: 10px; }
  table {
    width: 100%;
    border-collapse: collapse;
    table-layout: fixed;
  }
  th, td {
    border: 1px solid #e2e8f0;
    padding: 3px 5px;
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }
  thead th {
    background: #1e3a8a;
    color: #fff;
    font-weight: 600;
    text-align: right;
  }
  thead th:first-child { text-align: left; }
  td.label { text-align: left; }
  td.num { text-align: right; font-variant-numeric: tabular-nums; }
  td.year { background: #f1f5f9; font-weight: 600; }
  tr.aggregate td { background: #f1f5f9; font-weight: 600; }
  tr.aggregate td.year { background: #e2e8f0; }
  tr.section td {
    background: #dbeafe;
    color: #1e3a8a;
    font-weight: 700;
    padding: 5px 6px;
    font-size: 10px;
  }
  tr.section.section-bilanz td { background: #fef3c7; color: #92400e; }
  tr.section.section-cashflow td { background: #e0e7ff; }
  tr.section.section-liqui td { background: #ccfbf1; color: #134e4a; }
  td.indent { padding-left: 14px; color: #475569; }
  td.ist { color: #0f172a; }
  td.soll { color: #1d4ed8; }
  td.plan { color: #1d4ed8; }
  table.banken th.month-head { background: #334155; }
  table.banken th.sub { background: #475569; font-size: 8px; }
  td.delta, .delta { font-size: 7px; font-weight: 600; }
  /* Abweichung nur 50% Deckkraft (gemaess Vorgabe) */
  .delta.pos { color: rgba(5,150,105,0.5); }
  .delta.neg { color: rgba(239,68,68,0.5); }
  .muted { color: #94a3b8; font-weight: normal; font-size: 8px; }
  .legend {
    margin-top: 4mm;
    color: #64748b;
    font-size: 9px;
  }
  .legend span { margin-right: 12px; }
  .legend .dot {
    display: inline-block;
    width: 8px; height: 8px;
    border-radius: 50%;
    margin-right: 4px;
    vertical-align: middle;
  }
  .year-block { page-break-before: always; }
  .year-block:first-child { page-break-before: avoid; }
  @media print {
    body { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
    thead { display: table-header-group; }
    tr { page-break-inside: avoid; }
  }
</style>
</head>
<body>
${blocks.join('\n')}
<script>
  window.addEventListener('load', () => {
    setTimeout(() => window.print(), 250);
  });
</script>
</body>
</html>`
}

function escapeHtml(s: string): string {
  return s
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
}

// ─── Einheitlicher Mehrjahres-Export (Excel + PDF, Standard + Banken) ──────

/**
 * Exportiert einen oder mehrere Plan-Jahre als Excel oder PDF, im Standard-
 * oder Banken-Layout. Pro Jahr ein eigenes Excel-Blatt bzw. eine eigene
 * PDF-Seite.
 */
export async function exportPlans(
  datasets: PlanComparisonResponse[],
  opts: { format: ExportFormat; layout: ExportLayout },
): Promise<void> {
  if (datasets.length === 0) return
  const company = datasets[0]!.plan.companyKey
  const years = datasets.map((d) => d.plan.year).sort((a, b) => a - b)
  const yearLabel = years.length === 1 ? String(years[0]) : `${years[0]}-${years[years.length - 1]}`
  const layoutLabel = opts.layout === 'banken' ? 'Banken' : 'Plan'

  if (opts.format === 'excel') {
    const buf = await buildExcelBuffer(datasets, opts.layout)
    const date = new Date().toISOString().slice(0, 10)
    downloadBuffer(buf, `Liqui${layoutLabel}_${safeFilenamePart(company)}_${yearLabel}_${date}.xlsx`)
    return
  }

  // PDF
  const blocks = datasets.map((ds) =>
    opts.layout === 'banken' ? pdfTableBanken(ds) : pdfTableStandard(ds),
  )
  const html = pdfShell(`Liquiditätsplanung ${company} ${yearLabel}`, blocks)
  const w = window.open('', '_blank', 'width=1400,height=900')
  if (!w) {
    alert('Pop-up blockiert — bitte Pop-ups für diese Seite erlauben, um den PDF-Export zu nutzen.')
    return
  }
  w.document.open()
  w.document.write(html)
  w.document.close()
}
